import React, { useState, useEffect } from 'react';
import { BookOpen, FileText, Repeat, PartyPopper } from 'lucide-react';

export default function QuickUpdateCard({ 
  currentBook, 
  onProgressUpdate, 
  onBookCompletion, 
  onBookSwitch 
}) {
  const [pageInput, setPageInput] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (currentBook?.current_page) {
      setPageInput(currentBook.current_page.toString());
    }
  }, [currentBook]);

  const handlePageUpdate = async () => {
    if (!currentBook || !pageInput || isUpdating) return;
    
    const newPage = parseInt(pageInput);
    if (isNaN(newPage) || newPage < 0) return;
    
    setIsUpdating(true);
    try {
      await onProgressUpdate(currentBook.id, newPage);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleBookCompletion = async () => {
    if (!currentBook || isUpdating) return;
    
    setIsUpdating(true);
    try {
      await onBookCompletion(currentBook.id);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handlePageUpdate();
    }
  };

  if (!currentBook) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-amber-200 shadow-sm">
        <div className="text-center py-8">
          <BookOpen className="w-12 h-12 text-stone-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No Book Selected</h3>
          <p className="text-stone-600 mb-4">Choose a book to start your reading challenge!</p>
          <button 
            onClick={onBookSwitch}
            className="px-6 py-3 bg-amber-500 text-white font-semibold rounded-xl hover:bg-amber-600 transition-colors"
          >
            Choose a Book
          </button>
        </div>
      </div>
    );
  }

  const progressPercentage = currentBook.total_pages 
    ? Math.round((currentBook.current_page / currentBook.total_pages) * 100)
    : 0;

  return (
    <div className="bg-white rounded-2xl p-5 border border-amber-200 shadow-sm">
      <div className="text-center mb-4">
        <div className="flex items-center justify-center gap-2 mb-3">
          <BookOpen className="w-5 h-5 text-amber-600" />
          <h2 className="text-lg font-semibold text-slate-900">Quick Update</h2>
        </div>
      </div>

      {/* Current Book Display */}
      <div className="bg-amber-50 rounded-xl p-4 mb-4 text-center">
        <h3 className="text-lg font-bold text-slate-900 mb-1">{currentBook.book_title}</h3>
        {currentBook.book_author && (
          <p className="text-sm text-amber-700 mb-2">by {currentBook.book_author}</p>
        )}
        
        <div className="mb-3">
          <p className="text-sm text-amber-600 font-medium">
            Currently on page {currentBook.current_page}
            {currentBook.total_pages && ` of ${currentBook.total_pages}`}
          </p>
          
          {currentBook.total_pages && (
            <div className="mt-2">
              <div className="w-full bg-amber-200 rounded-full h-2">
                <div 
                  className="bg-amber-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
              <p className="text-xs text-amber-600 mt-1">{progressPercentage}% complete</p>
            </div>
          )}
        </div>

        {/* Page Input */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <label className="text-sm font-medium text-stone-600">I'm now on page:</label>
          <input
            type="number"
            value={pageInput}
            onChange={(e) => setPageInput(e.target.value)}
            onKeyPress={handleKeyPress}
            className="w-20 h-12 border-2 border-amber-300 rounded-xl text-center text-lg font-semibold text-slate-900 bg-white focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
            placeholder={currentBook.current_page?.toString() || "0"}
            min="0"
            max={currentBook.total_pages || 9999}
            disabled={isUpdating}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={handlePageUpdate}
          disabled={isUpdating || !pageInput || pageInput === currentBook.current_page?.toString()}
          className="flex flex-col items-center gap-2 p-4 bg-amber-500 text-white rounded-xl font-semibold transition-all duration-200 hover:bg-amber-600 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FileText className="w-5 h-5" />
          <span className="text-sm">
            {isUpdating ? 'Updating...' : 'Update Page'}
          </span>
        </button>

        <button
          onClick={onBookSwitch}
          disabled={isUpdating}
          className="flex flex-col items-center gap-2 p-4 bg-azure-500 text-white rounded-xl font-semibold transition-all duration-200 hover:bg-azure-600 active:scale-95 disabled:opacity-50"
        >
          <Repeat className="w-5 h-5" />
          <span className="text-sm">Switch Book</span>
        </button>

        <button
          onClick={handleBookCompletion}
          disabled={isUpdating}
          className="col-span-2 flex flex-col items-center gap-2 p-4 bg-emerald-500 text-white rounded-xl font-semibold transition-all duration-200 hover:bg-emerald-600 active:scale-95 disabled:opacity-50"
        >
          <PartyPopper className="w-5 h-5" />
          <span className="text-sm">
            {isUpdating ? 'Completing...' : 'I Finished This Book!'}
          </span>
        </button>
      </div>

      {/* Quick Progress Indicators */}
      {currentBook.total_pages && (
        <div className="mt-4 flex justify-center gap-4 text-xs text-stone-500">
          <span>
            {currentBook.total_pages - currentBook.current_page} pages left
          </span>
          <span>•</span>
          <span>
            {Math.ceil((currentBook.total_pages - currentBook.current_page) / 20)} days at 20 pages/day
          </span>
        </div>
      )}
    </div>
  );
}